import { useEffect, useMemo, useState } from "react";
import {
  fetchEmployeeDailyHoursBatch,
  fetchMontageplanOvertimeBalances,
} from "@/lib/employeeTimes";
import { toIso } from "./dateUtils";

const CHUNK_DAYS = 21;

export type DailyHoursMap = Map<string, Map<string, number>>;
export type DailyPaidHoursMap = Map<string, Map<string, number>>;
export type OvertimeBalanceMap = Map<string, number>;

export interface MontageplanTimesOverlay {
  hoursByLoginDate: DailyHoursMap;
  paidHoursByLoginDate: DailyPaidHoursMap;
  overtimeByLogin: OvertimeBalanceMap;
}

function mergeHours(
  prev: DailyHoursMap,
  rows: Array<{ user_login: string; work_date: string; hours: number; paid_hours?: number }>,
  field: "hours" | "paid_hours",
): DailyHoursMap {
  const next = new Map(prev);
  for (const row of rows) {
    const login = row.user_login.trim().toLowerCase();
    const iso = row.work_date.slice(0, 10);
    const hours = Number(field === "hours" ? row.hours : row.paid_hours ?? 0);
    if (!login || !iso || !Number.isFinite(hours) || hours <= 0) continue;
    const byDate = new Map(next.get(login) ?? []);
    byDate.set(iso, hours);
    next.set(login, byDate);
  }
  return next;
}

/** Lädt Tages-Arbeitszeiten (App+Hero) und Überstundenstand – blockiert nicht den Montageplan-Load. */
export function useMontageplanTimesOverlay(
  days: Date[],
  employeeLogins: string[],
  enabled = true,
): MontageplanTimesOverlay {
  const [hoursByLoginDate, setHoursByLoginDate] = useState<DailyHoursMap>(new Map());
  const [paidHoursByLoginDate, setPaidHoursByLoginDate] = useState<DailyPaidHoursMap>(new Map());
  const [overtimeByLogin, setOvertimeByLogin] = useState<OvertimeBalanceMap>(new Map());

  const dayIsos = useMemo(() => days.map((d) => toIso(d)), [days]);
  const loginsKey = useMemo(
    () => [...new Set(employeeLogins.map((l) => l.trim().toLowerCase()).filter(Boolean))].sort().join(","),
    [employeeLogins],
  );

  useEffect(() => {
    if (!enabled || !loginsKey) {
      setOvertimeByLogin(new Map());
      return;
    }

    let cancelled = false;
    const logins = loginsKey.split(",");

    void (async () => {
      try {
        const result = await fetchMontageplanOvertimeBalances({ user_logins: logins });
        if (cancelled) return;
        const next = new Map<string, number>();
        for (const [login, hours] of Object.entries(result.overtime_balance_hours ?? {})) {
          const key = login.trim().toLowerCase();
          const value = Number(hours);
          if (!key || !Number.isFinite(value)) continue;
          next.set(key, value);
        }
        setOvertimeByLogin(next);
      } catch {
        if (!cancelled) setOvertimeByLogin(new Map());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, loginsKey]);

  useEffect(() => {
    if (!enabled || !loginsKey || dayIsos.length === 0) {
      setHoursByLoginDate(new Map());
      setPaidHoursByLoginDate(new Map());
      return;
    }

    let cancelled = false;
    const logins = loginsKey.split(",");
    const todayIso = toIso(new Date());
    const todayIdx = dayIsos.indexOf(todayIso);

    const pastIsos: string[] = [];
    const futureIsos: string[] = [];
    if (todayIdx >= 0) {
      for (let i = todayIdx; i >= 0; i -= 1) {
        const iso = dayIsos[i];
        if (iso) pastIsos.push(iso);
      }
      for (let i = todayIdx + 1; i < dayIsos.length; i += 1) {
        const iso = dayIsos[i];
        if (iso) futureIsos.push(iso);
      }
    } else {
      pastIsos.push(...dayIsos.filter(Boolean));
    }

    const chunks: Array<{ from: string; to: string }> = [];
    for (let start = 0; start < pastIsos.length; start += CHUNK_DAYS) {
      const slice = pastIsos.slice(start, start + CHUNK_DAYS);
      const from = slice[slice.length - 1];
      const to = slice[0];
      if (from && to) chunks.push({ from, to });
    }
    if (futureIsos.length > 0) {
      const from = futureIsos[0];
      const to = futureIsos[futureIsos.length - 1];
      if (from && to) chunks.push({ from, to });
    }

    async function loadAll() {
      await Promise.all(
        chunks.map(async (chunk) => {
          if (cancelled) return;
          try {
            const result = await fetchEmployeeDailyHoursBatch({
              date_from: chunk.from,
              date_to: chunk.to,
              user_logins: logins,
            });
            if (cancelled) return;
            setHoursByLoginDate((prev) => mergeHours(prev, result.hours, "hours"));
            setPaidHoursByLoginDate((prev) => mergeHours(prev, result.hours, "paid_hours"));
          } catch {
            // Overlay bleibt leer – Montageplan funktioniert weiter.
          }
        }),
      );
    }

    setHoursByLoginDate(new Map());
    setPaidHoursByLoginDate(new Map());
    void loadAll();

    return () => {
      cancelled = true;
    };
  }, [enabled, dayIsos, loginsKey]);

  return { hoursByLoginDate, paidHoursByLoginDate, overtimeByLogin };
}

/** @deprecated Alias – nutze useMontageplanTimesOverlay */
export function useMontageplanDailyHours(
  days: Date[],
  employeeLogins: string[],
  enabled = true,
): DailyHoursMap {
  return useMontageplanTimesOverlay(days, employeeLogins, enabled).hoursByLoginDate;
}

export function getDailyHoursForCell(
  map: DailyHoursMap,
  employeeLogin: string | null | undefined,
  dayIso: string,
): number | null {
  if (!employeeLogin) return null;
  const hours = map.get(employeeLogin.trim().toLowerCase())?.get(dayIso.slice(0, 10));
  if (hours == null || hours <= 0) return null;
  return hours;
}

export function getDailyPaidHoursForCell(
  map: DailyPaidHoursMap,
  employeeLogin: string | null | undefined,
  dayIso: string,
): number {
  if (!employeeLogin) return 0;
  const hours = map.get(employeeLogin.trim().toLowerCase())?.get(dayIso.slice(0, 10));
  if (hours == null || !Number.isFinite(hours) || hours <= 0) return 0;
  return hours;
}

export function getOvertimeBalanceForEmployee(
  map: OvertimeBalanceMap,
  employeeLogin: string | null | undefined,
): number | null {
  if (!employeeLogin) return null;
  const hours = map.get(employeeLogin.trim().toLowerCase());
  if (hours == null || !Number.isFinite(hours)) return null;
  return hours;
}

export function formatMontageplanDayHours(hours: number): string {
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (m === 0) return `${h}h`;
  return `${h}:${String(m).padStart(2, "0")}`;
}

export function formatMontageplanOvertimeBalance(hours: number): string {
  const sign = hours < 0 ? "−" : "";
  const abs = Math.abs(hours);
  const formatted =
    abs % 1 === 0
      ? String(Math.round(abs))
      : abs.toLocaleString("de-DE", { maximumFractionDigits: 1 });
  return `${sign}${formatted}h`;
}
